import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Download, Image as ImageIcon, X, ChevronLeft, ChevronRight,
  Loader2, AlertCircle, User, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { trackVisit } from '../lib/visitorTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const HeadshotRecipientGallery = () => {
  const { accessToken } = useParams();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Download all state
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Fetch gallery data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(
          `${BACKEND_URL}/api/headshot/${accessToken}`
        );

        setData(response.data);
      } catch (err) {
        console.error('Error fetching gallery:', err);
        if (err.response?.status === 404) {
          setError('expired');
        } else if (err.response?.status === 410) {
          setError('expired');
        } else {
          setError('error');
        }
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      fetchData();
      trackVisit(`${BACKEND_URL}/api/headshot/${accessToken}/track`);
    }
  }, [accessToken]);

  // Download single photo
  const handleDownload = async (photo) => {
    try {
      toast.info('Starting download...');

      // Fetch the image directly from CloudFront URL
      const imageUrl = photo.original_url || photo.preview_url;
      if (!imageUrl) {
        toast.error('Photo URL not available');
        return;
      }

      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }

      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = photo.filename || `photo-${photo.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Log the download via API (fire and forget)
      axios.get(`${BACKEND_URL}/api/headshot/${accessToken}/download/${photo.id}`).catch(() => {});

      toast.success('Download complete!');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download photo');
    }
  };

  // Download all matched photos
  const handleDownloadAll = async () => {
    if (downloadingAll) return;

    try {
      setDownloadingAll(true);
      toast.info('Preparing your download...');

      const response = await axios.post(
        `${BACKEND_URL}/api/headshot/${accessToken}/download-all`
      );

      if (response.data.status === 'pending') {
        toast.success(
          `Your ZIP file with ${response.data.photo_count} photos is being prepared. You'll receive an email when it's ready!`,
          { duration: 6000 }
        );
      }
    } catch (err) {
      console.error('Download all error:', err);
      if (err.response?.status === 400) {
        toast.error('No photos available to download');
      } else {
        toast.error('Failed to start download. Please try again.');
      }
    } finally {
      setDownloadingAll(false);
    }
  };

  // Lightbox navigation
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextPhoto = () => {
    if (data?.photos) {
      setLightboxIndex((prev) => (prev + 1) % data.photos.length);
    }
  };

  const prevPhoto = () => {
    if (data?.photos) {
      setLightboxIndex((prev) => (prev - 1 + data.photos.length) % data.photos.length);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextPhoto();
      if (e.key === 'ArrowLeft') prevPhoto();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, data]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F4F2EE' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#BF212F] mx-auto mb-4" />
          <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 11, color: '#8A8884' }}>Loading your photos…</p>
        </div>
      </div>
    );
  }

  // Error state - expired or not found
  if (error === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 font-tiq-body" style={{ background: '#F4F2EE' }}>
        <Toaster position="top-center" richColors />
        <div className="text-center max-w-md" style={{ background: '#fff', border: '1px solid #E2DED6', borderRadius: 14, padding: 'clamp(28px,4vw,40px)' }}>
          <div className="flex items-center justify-center mx-auto mb-6" style={{ width: 72, height: 72, borderRadius: '50%', background: '#FEF3C7' }}>
            <Clock className="w-9 h-9" style={{ color: '#92400E' }} />
          </div>
          <h1 className="font-tiq-display uppercase mb-3" style={{ color: '#141414', fontWeight: 800, fontSize: 24, letterSpacing: '-0.015em' }}>Link expired</h1>
          <p className="mb-6" style={{ color: '#55555C' }}>
            This photo delivery link has expired or is no longer valid. If you need access to your photos, please contact the event organiser.
          </p>
          <img src="/turbo-logo-color.png" alt="Turbo 360" className="h-8 mx-auto" />
        </div>
      </div>
    );
  }

  // Generic error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 font-tiq-body" style={{ background: '#F4F2EE' }}>
        <Toaster position="top-center" richColors />
        <div className="text-center max-w-md" style={{ background: '#fff', border: '1px solid #E2DED6', borderRadius: 14, padding: 'clamp(28px,4vw,40px)' }}>
          <div className="flex items-center justify-center mx-auto mb-6" style={{ width: 72, height: 72, borderRadius: '50%', background: '#FEE2E2' }}>
            <AlertCircle className="w-9 h-9" style={{ color: '#BF212F' }} />
          </div>
          <h1 className="font-tiq-display uppercase mb-3" style={{ color: '#141414', fontWeight: 800, fontSize: 24, letterSpacing: '-0.015em' }}>Something went wrong</h1>
          <p className="mb-6" style={{ color: '#55555C' }}>We couldn't load your photos. Please try refreshing the page or contact support.</p>
          <Button onClick={() => window.location.reload()} className="font-tiq-heading text-white" style={{ background: '#BF212F' }}>Try again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-tiq-body" style={{ background: '#F4F2EE' }}>
      <Toaster position="top-center" richColors />

      {/* Header — white, colour logo */}
      <header className="sticky top-0 z-40" style={{ background: '#fff', borderBottom: '1px solid #E2DED6' }}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-center">
          <img src="/turbo-logo-color.png" alt="Turbo 360" className="h-8" />
        </div>
      </header>

      {/* Hero — red → dark-red gradient */}
      <div className="relative overflow-hidden text-white text-center px-4" style={{ background: 'linear-gradient(135deg,#BF212F,#8B1923)', paddingTop: 'clamp(40px,6vw,64px)', paddingBottom: 'clamp(40px,6vw,64px)' }}>
        <img src="/turbo-mark-white.png" alt="" className="absolute pointer-events-none" style={{ top: '-40px', right: '-40px', width: 'min(34vw,340px)', opacity: 0.1, transform: 'rotate(-8deg)' }} />
        <div className="max-w-3xl mx-auto relative">
          {data?.headshot_thumbnail_url && (
            <div className="mx-auto mb-5 overflow-hidden" style={{ width: 96, height: 96, borderRadius: '50%', border: '4px solid #fff', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
              <img src={data.headshot_thumbnail_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <h1 className="font-tiq-display uppercase m-0" style={{ fontWeight: 900, fontSize: 'clamp(30px,5vw,52px)', lineHeight: 0.95, letterSpacing: '-0.025em' }}>
            Hi {data?.recipient_name?.split(' ')[0]}!
          </h1>
          <p className="mt-4" style={{ color: 'rgba(255,255,255,0.9)', fontSize: 'clamp(15px,2vw,18px)' }}>Here are your photos from</p>
          <p className="font-tiq-heading mt-1" style={{ fontWeight: 700, fontSize: 'clamp(18px,2.4vw,24px)' }}>{data?.gallery_name}</p>
          <p className="font-tiq-mono tiq-kicker mt-4" style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>
            {data?.photo_count} photo{data?.photo_count !== 1 ? 's' : ''} found
          </p>
          {data?.photo_count > 0 && (
            <div className="mt-6">
              <button
                onClick={handleDownloadAll} disabled={downloadingAll}
                className="font-tiq-heading inline-flex items-center gap-2"
                style={{ background: '#fff', color: '#BF212F', borderRadius: 10, padding: '13px 24px', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
              >
                {downloadingAll ? (<><Loader2 className="w-5 h-5 animate-spin" /> Preparing…</>) : (<><Download className="w-5 h-5" /> Download all {data?.photo_count} photos</>)}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Photo Grid — uniform, light */}
      <main className="max-w-7xl mx-auto" style={{ padding: 'clamp(24px,4vw,40px) clamp(8px,2vw,16px)' }}>
        {data?.photos?.length > 0 ? (
          <div className="tiq-uniform-grid" style={{ gap: 8 }}>
            {data.photos.map((photo, index) => (
              <div
                key={photo.id}
                className="group relative overflow-hidden cursor-pointer tiq-fade"
                style={{ aspectRatio: '1 / 1', background: '#E2DED6' }}
                onClick={() => openLightbox(index)}
              >
                <img
                  src={photo.thumbnail_url}
                  alt={photo.filename}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-end justify-end opacity-0 group-hover:opacity-100 transition-all duration-300" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.55))', padding: 10 }}>
                  <button onClick={(e) => { e.stopPropagation(); handleDownload(photo); }} title="Download" style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#BF212F', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <Download className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center" style={{ padding: '64px 20px' }}>
            <ImageIcon className="w-14 h-14 mx-auto mb-4" style={{ color: '#B9B6AF' }} />
            <h3 className="font-tiq-display uppercase mb-2" style={{ color: '#141414', fontWeight: 800, fontSize: 20 }}>No photos found</h3>
            <p style={{ color: '#8A8884' }}>We couldn't find any matching photos for you.</p>
          </div>
        )}
      </main>

      {/* Footer (light, signal bar) */}
      <footer className="flex flex-col items-center gap-[14px]" style={{ borderTop: '1px solid #E2DED6', padding: 'clamp(28px,4vw,40px) 24px', marginTop: 24 }}>
        <div className="tiq-signal-bar" style={{ width: 120, height: 4, borderRadius: 2 }} />
        <a href="https://turbo.net.au/?utm_source=iq.turbo.net.au&utm_medium=referral&utm_campaign=client_gallery&utm_content=headshot_footer" target="_blank" rel="noopener">
          <img src="/turbo-logo-color.png" alt="Turbo 360" className="h-6 hover:opacity-80 transition-opacity" />
        </a>
        <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 10, letterSpacing: '0.16em', color: '#B9B6AF' }}>Photos delivered by Turbo IQ</p>
      </footer>

      {/* Lightbox */}
      {lightboxOpen && data?.photos?.[lightboxIndex] && (
        <div
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation */}
          {data.photos.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevPhoto();
                }}
                className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextPhoto();
                }}
                className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          {/* Image */}
          <img
            src={data.photos[lightboxIndex].preview_url || data.photos[lightboxIndex].original_url}
            alt={data.photos[lightboxIndex].filename}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Bottom Bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <div className="text-white">
                <p className="text-sm opacity-80">
                  {lightboxIndex + 1} of {data.photos.length}
                </p>
                <p className="font-medium">
                  {data.photos[lightboxIndex].filename}
                </p>
              </div>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(data.photos[lightboxIndex]);
                }}
                className="bg-white text-slate-900 hover:bg-slate-100"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeadshotRecipientGallery;
